import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import { FaEnvelope } from "react-icons/fa";
import { FiRefreshCw, FiWifiOff } from "react-icons/fi";
import { Link } from "react-router-dom";
import { API_BASE } from "../config/api";

export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [offline, setOffline] = useState(false);
  const [sentTo, setSentTo] = useState(""); // email we sent to
  const [cooldown, setCooldown] = useState(0); // resend cooldown (s)
  const abortRef = useRef(null);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const initialValues = useMemo(() => ({ email: "" }), []);
  const validationSchema = Yup.object({
    email: Yup.string().trim().lowercase().email("Invalid email").required("Required"),
  });

  const cancelInflight = () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    return abortRef.current.signal;
  };

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    setOffline(false);
    const email = values.email.trim().toLowerCase();
    const signal = cancelInflight();

    try {
      await axios.post(
        `${API_BASE}/auth/forgot-password`,
        { email },
        { signal, timeout: 12000 }
      );

      setSentTo(email);
      setCooldown(60);
      toast.success("Password reset link sent! Check your inbox.");
      resetForm();
    } catch (error) {
      if (axios.isCancel(error)) return;

      if (error.code === "ECONNABORTED") {
        setOffline(true);
        toast.error("Request timed out. Please try again.");
      } else if (error.message === "Network Error") {
        setOffline(true);
        toast.error("Network error. Are you offline?");
      } else {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to send reset link.";
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!sentTo || cooldown > 0) return;
    setLoading(true);
    setOffline(false);
    const signal = cancelInflight();
    try {
      await axios.post(
        `${API_BASE}/auth/forgot-password`,
        { email: sentTo },
        { signal, timeout: 12000 }
      );
      setCooldown(60);
      toast.success("We sent you another link.");
    } catch (error) {
      if (axios.isCancel(error)) return;

      if (error.code === "ECONNABORTED") {
        setOffline(true);
        toast.error("Request timed out. Please try again.");
      } else if (error.message === "Network Error") {
        setOffline(true);
        toast.error("Network error. Are you offline?");
      } else {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to resend link.";
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-100 via-sky-100 to-white p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl p-8 w-full max-w-md space-y-6"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100, damping: 14 }}
      >
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-blue-800">
            Forgot Password
          </h1>
          <p className="text-slate-600 mt-1">
            Enter your email and we’ll send you a reset link.
          </p>
        </div>

        {offline && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-800">
            <FiWifiOff />
            <span>We couldn’t reach the server. Check your internet and try again.</span>
          </div>
        )}

        {/* Form */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange
        >
          {({ isSubmitting }) => (
            <Form className="space-y-5">
              <div className="relative">
                <Field
                  as={TextField}
                  name="email"
                  type="email"
                  label="Email address"
                  autoComplete="email"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaEnvelope />
                      </InputAdornment>
                    ),
                  }}
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="text-red-500 text-sm ml-1 mt-1"
                />
              </div>

              <motion.button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-full transition flex items-center justify-center"
                whileTap={{ scale: 0.98 }}
                disabled={loading || isSubmitting}
              >
                {loading ? (
                  <motion.div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Send Reset Link"
                )}
              </motion.button>
            </Form>
          )}
        </Formik>

        {/* Success area */}
        {sentTo && (
          <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-green-800">
            <p>
              If an account exists for <span className="font-semibold">{sentTo}</span>, a reset
              link will arrive shortly. Please check your inbox and spam folder.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                size="small"
                variant="text"
                startIcon={<FiRefreshCw />}
                onClick={handleResend}
                disabled={cooldown > 0 || loading}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend link"}
              </Button>
            </div>
          </div>
        )}

        <div className="text-center text-sm">
          <Link className="text-blue-700 hover:underline" to="/login">
            Back to Login
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
}
